import React, { useState, useEffect } from 'react';
import { useVoiceUpdate } from '../../hooks/useVoiceUpdate';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Menu, MessageSquare } from 'lucide-react';
import { useChatInterface } from '@/contexts/ChatInterfaceContext';

interface VoiceUpdateInterfaceProps {
  positId: string;
  onEndCall: () => void;
}

export const VoiceUpdateInterface: React.FC<VoiceUpdateInterfaceProps> = ({ 
  positId, 
  onEndCall 
}) => {
    const {
    isConnected,
    isConnecting,
    isAgentSpeaking,
    isProcessing,
    canRetry,
    startVoiceUpdate,
    endVoiceUpdate,
    knowledgeUpdates
  } = useVoiceUpdate(positId);

  const { toggleSidebar } = useChatInterface();

  // Auto-start conversation when component mounts
  useEffect(() => {
    if (positId) {
      startVoiceUpdate();
    }
  }, [positId, startVoiceUpdate]);

  const handleEndCall = async () => {
    endVoiceUpdate();
    onEndCall();
  };

  const handleRetry = async () => {
    if (canRetry) {
      startVoiceUpdate();
    }
  };

  // Show retry option if connection failed and retries are available
  if (!isConnected && !canRetry) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={onEndCall}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              <h1 className="text-lg font-semibold">Voice Update</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleSidebar}
              className="gap-2"
            >
              <Menu className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center bg-background px-4">
          <div className="text-center mb-8">
            <div className="text-red-600 text-lg mb-4">Connection failed</div>
            <div className="text-muted-foreground mb-6 max-w-md">
              Unable to connect to voice service. Please check your connection and try again.
            </div>
            
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleRetry}
                className="bg-violet-600 hover:bg-violet-700 text-white font-medium py-3 px-6 rounded-lg"
              >
                Try Again
              </button>
              <button
                onClick={onEndCall}
                className="bg-slate-600 hover:bg-slate-700 text-white font-medium py-3 px-6 rounded-lg"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEndCall}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Voice Update</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleSidebar}
            className="gap-2"
          >
            <Menu className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Voice Update Interface Content */}
      <div className="flex-1 flex items-center justify-center bg-background px-4">
        <div className="text-center mb-8">
          {/* Update-specific status indicator with modern colors */}
          <div className="mb-6">
            <div className={`w-24 h-24 rounded-full border-4 mx-auto mb-4 ${
              isConnected 
                ? isProcessing
                  ? 'border-violet-500 bg-violet-50 animate-pulse'
                  : isAgentSpeaking 
                    ? 'border-blue-600 bg-blue-50 animate-pulse' 
                    : 'border-emerald-500 bg-emerald-50'
                : 'border-gray-400 bg-gray-50 animate-spin'
            }`}>
              <div className={`w-4 h-4 rounded-full mx-auto mt-8 ${
                isConnected
                  ? isProcessing
                    ? 'bg-violet-500'
                    : isAgentSpeaking
                      ? 'bg-blue-600'
                      : 'bg-emerald-500'
                  : 'bg-gray-400'
              }`} />
            </div>
            
            <div className="text-xl font-medium text-foreground mb-2">
              {isConnected 
                ? isProcessing 
                  ? 'Building Knowledge...' 
                  : 'Voice Update Active' 
                : 'Connecting...'}
            </div>
            
            <div className="text-sm text-muted-foreground">
              {isConnected 
                ? isProcessing
                  ? 'Processing your input and updating Posit knowledge'
                  : isAgentSpeaking
                    ? 'AI is responding...'
                    : 'Share your knowledge and experience'
                : 'Setting up voice connection...'}
            </div>
          </div>

          {/* Knowledge update indicators */}
          {knowledgeUpdates.length > 0 && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg max-w-md mx-auto">
              <h3 className="text-sm font-medium text-emerald-800 mb-2">
                Knowledge Updated:
              </h3>
                             <div className="space-y-1">
                 {knowledgeUpdates.map((update, index) => (
                   <div key={index} className="text-xs text-emerald-700 bg-emerald-100 px-2 py-1 rounded">
                     {update}
                   </div>
                 ))}
               </div>
            </div>
          )}

          {/* End call button */}
          <button
            onClick={handleEndCall}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-4 px-8 rounded-full text-lg min-w-[120px]"
          >
            End Session
          </button>
        </div>
      </div>
    </div>
  );
}; 